import LineArrowsRightDownIcon from 'components/Luxkit/Icons/line/LineArrowsRightDownIcon'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LineUsersAltIcon from 'components/Luxkit/Icons/line/LineUsersAltIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import { isHotelPackage } from 'lib/offer/offerTypes'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryDown } from 'components/utils/breakpoint'

const StyledBodyTextBlock = styled(BodyTextBlock)`
  ${mediaQueryDown.mobile} {
    max-width: ${rem(320)};
  }
`

interface RoomPackageDetails {
  size?: string
  capacity?: string
  beds?: string
  wifi?: string
  parking?: string
}

function extractHotelRoomPackageDetailList(roomPackage: App.HotelPackage): RoomPackageDetails {
  const dimension = roomPackage.roomType.sizeSqm
  return {
    size: dimension ? `${dimension}m²` : undefined,
  }
}

function extractBedbankRoomPackageMaxCapacities(capacities: Array<App.RoomCapacity>): Omit<App.RoomCapacity, 'infants'> {
  let maxTotal = 0
  let maxAdults = 0
  let maxChildren = 0

  for (const capacity of capacities) {
    if (capacity.adults > maxAdults) {
      maxAdults = capacity.adults
    }
    if (capacity.children > maxChildren) {
      maxChildren = capacity.children
    }
    const total = capacity.adults + capacity.children
    if (total > maxTotal) {
      maxTotal = total
    }
  }

  return { total: maxTotal, adults: maxAdults, children: maxChildren }
}
function extractBedbankRoomPackageDetailList(roomPackage: App.BedbankPackage): RoomPackageDetails {
  const features = new Map(roomPackage.features.map((f) => ([f.id, f.text])))
  const maxCapacities = extractBedbankRoomPackageMaxCapacities(roomPackage.capacities.combinations)

  // TODO: Where does roomPackage.bedGroupsDescription go?

  return {
    beds: features.get('bed'),
    capacity: `Sleeps ${maxCapacities.total}`,
    size: features.get('dimension'),
    wifi: features.get('wifi'),
    parking: features.get('parking'),
  }
}

interface Props {
  roomPackage: App.HotelPackage | App.BedbankPackage
}

function AccommodationRoomDetailsList(props: Props) {
  const { roomPackage } = props
  const detailsList = useMemo(() => {
    if (isHotelPackage(roomPackage)) {
      return extractHotelRoomPackageDetailList(roomPackage)
    } else { // is BedbankPackage
      return extractBedbankRoomPackageDetailList(roomPackage)
    }
  }, [roomPackage])

  return <Group direction="vertical" horizontalAlign="start" gap={4} noDisplayWhenEmpty>
    <LabelGroup>
      {detailsList.size && <StructureLabel variant="light">
        <BodyTextBlock variant="medium" startIcon={<LineArrowsRightDownIcon />}>
          {detailsList.size}
        </BodyTextBlock>
      </StructureLabel>}
      {detailsList.capacity && <StructureLabel variant="light">
        <BodyTextBlock variant="medium" startIcon={<LineUsersAltIcon />}>
          {detailsList.capacity}
        </BodyTextBlock>
      </StructureLabel>}
      {detailsList.beds && <StructureLabel variant="light">
        <StyledBodyTextBlock variant="medium" startIcon={<LineBedIcon />} wrap="truncate">
          {detailsList.beds}
        </StyledBodyTextBlock>
      </StructureLabel>}
    </LabelGroup>
  </Group>
}

export default AccommodationRoomDetailsList
